import { useQuery } from '@tanstack/react-query';
import { api } from './api';

// ─────────────────────────────────────────────────────────────────────────────
// useOrgChecklist — the org setup checklist, TRANSPLANTED from
// components/wizard/OrgSetupWizard.tsx (now retired). Same derivation, same
// react-query keys (['orgs'], ['orgGuild', orgId], ['orgBranding', orgId],
// ['orgVetoConfig', orgId]) as the pages that actually own these controls —
// GuildConnectionPanel and pages/OrgBranding.tsx — so this never keeps a
// shadow "wizard progress" that could fall out of sync.
//
// `discordBound` is read off ['orgGuild', orgId] for a write-capable caller
// (matches GuildConnectionPanel's own query exactly, so a bind/unbind from
// inside Setup's Organisation tab is reflected here immediately) and falls
// back to the org summary's own primaryGuildId for anyone without write
// access to that endpoint.
//
// `branding` has no on-page anchor within TournamentSetup — accent colour and
// /ggveto map pools are configured on a separate route
// (/orgs/:orgId/branding), not inline on the Setup cockpit — so that item
// carries `externalHref` instead of a `tab`/`sectionId`; the checklist strip
// treats externalHref items as a plain navigation, not a same-page highlight.
//
// `publicPage` (added 2026-07-18, rules-defaults + public-pages surfacing
// goal): reads ['orgPublicPage', orgId] — the EXACT query key
// OrgPublicPageEditor.tsx itself uses to load its draft — so this can never
// drift from what that editor shows. Unlike `branding`, the editor is mounted
// INLINE inside OrgPane on this page's Organisation tab (not a separate
// route), so this item is an on-page `tab`/`sectionId` jump like
// identity/discord, not an externalHref.
// ─────────────────────────────────────────────────────────────────────────────

export type OrgChecklistItem = {
  id: 'identity' | 'discord' | 'branding' | 'publicPage';
  label: string;
  done: boolean;
  detail: string;
  tab?: 'org';
  sectionId?: string;
  externalHref?: string;
};

export type OrgChecklist = {
  items: OrgChecklistItem[];
  readyCount: number;
  totalCount: number;
  activeOrgId: string;
  orgExists: boolean;
};

export function useOrgChecklist(orgId?: string, canWriteRole = true): OrgChecklist {
  const { data: orgs } = useQuery({ queryKey: ['orgs'], queryFn: api.orgs });
  const orgList = orgs ?? [];
  const activeOrgId = orgId || orgList[0]?.id || '';
  const activeOrg = orgList.find(o => o.id === activeOrgId);
  const orgExists = !!activeOrg;

  const { data: guildData } = useQuery({
    queryKey: ['orgGuild', activeOrgId],
    queryFn: () => api.orgGuild(activeOrgId),
    enabled: !!activeOrgId && canWriteRole,
  });
  const discordBound = canWriteRole ? !!guildData?.primaryGuildId : !!activeOrg?.primaryGuildId;

  const { data: branding } = useQuery({
    queryKey: ['orgBranding', activeOrgId],
    queryFn: () => api.getOrgBranding(activeOrgId),
    enabled: !!activeOrgId,
  });
  const { data: vetoConfig } = useQuery({
    queryKey: ['orgVetoConfig', activeOrgId],
    queryFn: () => api.getOrgVetoConfig(activeOrgId),
    enabled: !!activeOrgId,
  });
  const brandingSet = !!branding?.accentColor || !!vetoConfig?.vetoConfig;

  // Public page — same query OrgPublicPageEditor.tsx mounts (['orgPublicPage',
  // orgId] → api.getOrgPublicPage(orgId), the authed draft CRUD endpoint, not
  // the public-facing /org/:slug route). The draft endpoint always 200s with
  // a { published: false, ... } default when nothing has been authored yet,
  // so "not authored" and "authored but unpublished" both read as
  // done:false/'not published' here without any special not-found handling.
  const { data: orgPublicPage } = useQuery({
    queryKey: ['orgPublicPage', activeOrgId],
    queryFn: () => api.getOrgPublicPage(activeOrgId),
    enabled: !!activeOrgId,
  });
  const publicPagePublished = !!orgPublicPage?.published;

  const items: OrgChecklistItem[] = [
    {
      id: 'identity',
      label: 'Org exists',
      done: orgExists,
      detail: orgExists ? activeOrg!.name : 'not created',
      tab: 'org',
      sectionId: 'org-identity',
    },
    {
      id: 'discord',
      label: 'Discord linked',
      done: orgExists && discordBound,
      detail: discordBound ? 'linked' : 'not linked',
      tab: 'org',
      sectionId: 'org-discord',
    },
    {
      id: 'branding',
      label: 'Veto/branding set',
      done: orgExists && brandingSet,
      detail: brandingSet ? 'configured' : 'defaults',
      externalHref: orgExists ? `/orgs/${encodeURIComponent(activeOrgId)}/branding` : undefined,
    },
    {
      id: 'publicPage',
      label: 'Public page',
      done: orgExists && publicPagePublished,
      detail: publicPagePublished ? 'published' : 'not published',
      tab: 'org',
      sectionId: 'org-publicpage',
    },
  ];

  return {
    items,
    readyCount: items.filter(i => i.done).length,
    totalCount: items.length,
    activeOrgId,
    orgExists,
  };
}
